import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import StatusBadge from '../../components/common/StatusBadge';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as clientTicketsApi from '../../api/clientTickets';
import * as employeesApi from '../../api/employees';
import { formatDateTime } from '../../utils/format';

const TYPE_OPTIONS = ['Missing Report', 'Upload Issue', 'Invoice Query', 'Audit Clarification'].map((v) => ({ value: v, label: v }));
const STATUS_OPTIONS = ['Open', 'In Progress', 'Resolved', 'Closed'].map((v) => ({ value: v, label: v }));

export default function ClientTickets() {
  const { actor } = useAuth();
  const isClient = actor.role === 'client';
  const canManage = !isClient;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'Audit Clarification', subject: '', description: '' });

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    clientTicketsApi
      .listClientTickets({})
      .then((res) => setRows(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load tickets.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await clientTicketsApi.createClientTicket(form);
      setSuccess('Support ticket submitted.');
      setShowForm(false);
      setForm({ type: 'Audit Clarification', subject: '', description: '' });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit ticket.');
    }
  };

  const openDetail = async (row) => {
    const res = await clientTicketsApi.getClientTicket(row.id);
    setSelectedTicket(res.data);
    if (canManage && employeeOptions.length === 0) {
      const empRes = await employeesApi.listEmployees({ limit: 500 });
      setEmployeeOptions(empRes.data.map((e) => ({ value: e.id, label: e.name })));
    }
  };

  const refreshDetail = async () => {
    const res = await clientTicketsApi.getClientTicket(selectedTicket.id);
    setSelectedTicket(res.data);
  };

  const handleStatusUpdate = async (status) => {
    try {
      await clientTicketsApi.updateClientTicket(selectedTicket.id, { status });
      await refreshDetail();
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update ticket.');
    }
  };

  const handleAssign = async (assignedEmployeeId) => {
    try {
      await clientTicketsApi.updateClientTicket(selectedTicket.id, { assignedEmployeeId });
      await refreshDetail();
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign ticket.');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      await clientTicketsApi.addClientTicketComment(selectedTicket.id, commentText.trim());
      setCommentText('');
      await refreshDetail();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add comment.');
    }
  };

  const columns = [
    { key: 'subject', label: 'Subject', render: (r) => <button type="button" className="btn-link" onClick={() => openDetail(r)}>{r.subject}</button> },
    { key: 'type', label: 'Type' },
    { key: 'client', label: 'Client', render: (r) => r.client?.clientName || '-' },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'assignee', label: 'Assigned To', render: (r) => r.assignee?.name || 'Unassigned' },
  ];

  return (
    <DashboardLayout title="Client Support">
      <div className="page-header">
        <div>
          <h1 className="heading">Client Support Tickets</h1>
          <p className="page-subtext">{isClient ? 'Questions and issues you have raised.' : 'Support requests submitted by clients.'}</p>
        </div>
        {isClient && <button type="button" className="btn" onClick={() => setShowForm(true)}>Raise a Ticket</button>}
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      {loading ? <Loader text="Loading tickets..." /> : <DataTable columns={columns} rows={rows} getRowKey={(r) => r.id} emptyMessage="No support tickets found." />}

      {showForm && (
        <Modal
          title="Raise a Support Ticket"
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="ct-form" className="btn">Submit</button>
            </>
          }
        >
          <form id="ct-form" onSubmit={handleCreate}>
            <div className="form-grid single-column">
              <FormField label="Type" type="select" options={TYPE_OPTIONS} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} />
              <FormField label="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />
              <FormField label="Description" type="textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </form>
        </Modal>
      )}

      {selectedTicket && (
        <Modal title={selectedTicket.subject} onClose={() => setSelectedTicket(null)} width={700}>
          <p className="text-muted">{selectedTicket.type} &middot; {selectedTicket.client?.clientName}</p>
          <p>{selectedTicket.description || 'No description.'}</p>

          {canManage && (
            <>
              <div className="btn-group" style={{ marginBottom: 12 }}>
                {STATUS_OPTIONS.map((s) => (
                  <button key={s.value} type="button" className={s.value === selectedTicket.status ? 'btn btn-small' : 'btn btn-secondary btn-small'} onClick={() => handleStatusUpdate(s.value)}>
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="form-field" style={{ maxWidth: 320 }}>
                <label>Assign To</label>
                <select value={selectedTicket.assignee?.id || ''} onChange={(e) => handleAssign(e.target.value)}>
                  <option value="">Unassigned</option>
                  {employeeOptions.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                </select>
              </div>
            </>
          )}
          {!canManage && <StatusBadge status={selectedTicket.status} />}

          <h3 className="subheading" style={{ marginTop: 16 }}>Comments</h3>
          <div className="comment-list">
            {(selectedTicket.comments || []).map((c) => (
              <div key={c.id} className="comment-item">
                <div className="comment-meta">{c.authorType === 'client' ? 'Client' : 'Staff'} &middot; {formatDateTime(c.createdAt)}</div>
                <div>{c.comment}</div>
              </div>
            ))}
            {(!selectedTicket.comments || selectedTicket.comments.length === 0) && <p className="text-muted">No comments yet.</p>}
          </div>
          <form onSubmit={handleAddComment} style={{ display: 'flex', gap: 10 }}>
            <input type="text" placeholder="Add a comment..." value={commentText} onChange={(e) => setCommentText(e.target.value)} />
            <button type="submit" className="btn btn-small">Post</button>
          </form>
        </Modal>
      )}
    </DashboardLayout>
  );
}
